import { Link } from '@inertiajs/react';
import { forwardRef, useContext } from 'react';
import { IntlContext } from 'react-intl';

/**
 * How a marketing href has to be rendered:
 *  - 'anchor'   same-page fragments and mailto:/tel: links, and this app's
 *               files rather than pages (a PDF, /sitemap.xml, /images/…,
 *               /storage/…, as a CMS article may link them). A plain <a>:
 *               an Inertia visit would fire a pointless request, or get a
 *               file back where it expects a page.
 *  - 'external' another origin (cal.com, the public passport viewer). A new tab.
 *  - 'internal' this app's pages. An Inertia <Link>.
 *
 * Ziggy's route() hands back absolute URLs, so "starts with http" cannot tell
 * internal from external on its own: the origin is compared instead. An
 * explicit `external` boolean overrides the detection.
 */
export function hrefKind(href, external) {
    if (/^(#|mailto:|tel:)/i.test(href)) return 'anchor';
    if (typeof external === 'boolean') return external ? 'external' : 'internal';
    if (isForeignOrigin(href)) return 'external';

    return isFile(href) ? 'anchor' : 'internal';
}

// A path that is a file, not a page: under the public file folders, or
// ending in an extension (a letter, then up to four letters or digits:
// ".pdf", ".xml", ".webp", not a slug's ".2").
const FILE_PATH = /^\/(images|storage|build|uploads)\/|\.[a-z][a-z0-9]{1,4}$/i;

function isFile(href) {
    try {
        const base = typeof window === 'undefined' ? 'http://localhost' : window.location.href;

        return FILE_PATH.test(new URL(href, base).pathname);
    } catch {
        return false;
    }
}

function isForeignOrigin(href) {
    if (!/^(https?:)?\/\//i.test(href)) return false;
    if (typeof window === 'undefined') return true;

    try {
        return new URL(href, window.location.href).origin !== window.location.origin;
    } catch {
        return true;
    }
}

/**
 * One link element for the whole marketing site: an Inertia <Link> for pages
 * of this app, a new-tab <a rel="noopener noreferrer"> for other origins and a
 * plain <a> for fragments, mail and phone links. A new tab is said to
 * assistive technology too, "(opens in a new tab)" after the link's text, as
 * the arrow says it to the eye (audit 44); outside the site's IntlProvider
 * (a bare test render) there is nothing to say it with.
 */
const SmartLink = forwardRef(function SmartLink({ href, external, children, ...props }, ref) {
    const kind = hrefKind(href, external);
    const intl = useContext(IntlContext);

    if (kind === 'internal') {
        return (
            <Link ref={ref} href={href} {...props}>
                {children}
            </Link>
        );
    }

    if (kind === 'external') {
        return (
            <a ref={ref} href={href} {...props} target="_blank" rel="noopener noreferrer">
                {children}
                {intl && <span className="sr-only"> ({intl.formatMessage({ id: 'site.footer.newTab' })})</span>}
            </a>
        );
    }

    return (
        <a ref={ref} href={href} {...props}>
            {children}
        </a>
    );
});

export default SmartLink;
